import '@/styles/gantt.css';
import { memo, useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns';
import { CalendarClock, ChevronRight, Crosshair, Plus, Unlink } from '@/components/ui/icons';
import type { Location, Task, View } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import { getDb, useDb, useDbShallow } from '@/lib/store';
import { effectiveStatuses, statusOf } from '@/lib/hierarchy';
import type { TaskGroup } from '@/lib/query';
import { addTaskLink, removeTaskLink, updateTask, updateViewSettings } from '@/lib/actions';
import { readableOn } from '@/lib/color';
import { useOpenTask } from '@/lib/nav';
import { Popover } from '@/components/ui/Popover';
import { Menu, MenuItem, MenuLabel } from '@/components/ui/Menu';
import { openContextMenu, toast } from '@/components/ui/overlays';
import { StatusIcon, StatusMenu, statusProgress } from '@/components/fields/Status';
import { TaskTypeIcon } from '@/components/fields/TaskCells';
import { ViewToolbar } from './ViewToolbar';
import { useViewQuery } from './useViewQuery';
import { TaskContextMenu } from '@/task/TaskContextMenu';
import { GroupLabel, createTaskInGroup, useSubtaskExpansion } from './table/shared';
import {
  BAR_H,
  HEAD_H,
  PX_PER_DAY,
  ROW_H,
  applyDrag,
  dayIndex,
  dayTs,
  desiredRange,
  dragLimits,
  fmtDay,
  mergeRange,
  monthSegments,
  spanOf,
  unitSegments,
  type DragMode,
  type Range,
  type Zoom,
} from './gantt/timeline';

const LEFT_KEY = 'atrium.gantt.leftWidth';
const LEFT_DEFAULT = 300;
const LABEL_INSIDE_MIN = 64;

type GRow =
  | { kind: 'group'; key: string; group: TaskGroup; collapsed: boolean }
  | { kind: 'task'; key: string; task: Task; depth: number; hasKids: boolean; expanded: boolean };

interface Geo {
  row: number;
  a: number;
  b: number;
  x1: number;
  x2: number;
  milestone: boolean;
  start: number | null;
  due: number | null;
}

interface BarInfo {
  color: string;
  closed: boolean;
  milestone: boolean;
}

interface DragState {
  taskId: string;
  mode: DragMode;
  delta: number;
}

interface LinkDrag {
  fromId: string;
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  overId: string | null;
}

function readLeftWidth() {
  try {
    const v = Number(localStorage.getItem(LEFT_KEY));
    return v >= 200 && v <= 700 ? v : LEFT_DEFAULT;
  } catch {
    return LEFT_DEFAULT;
  }
}

/** Elbow connector from the end of one bar to the start of another. */
function elbowPath(x1: number, y1: number, x2: number, y2: number) {
  const gap = 10;
  if (x2 - gap >= x1 + gap) {
    const mx = x1 + gap;
    return `M${x1},${y1} H${mx} V${y2} H${x2}`;
  }
  // target starts before the source ends: route around through the gap between rows
  const midY = y2 > y1 ? y2 - ROW_H / 2 : y2 + ROW_H / 2;
  return `M${x1},${y1} H${x1 + gap} V${midY} H${x2 - gap} V${y2} H${x2}`;
}

function createDependency(fromId: string, toId: string) {
  if (fromId === toId) return;
  const exists = Object.values(getDb().taskLinks).some(
    (l) => (l.fromTaskId === fromId && l.toTaskId === toId) || (l.fromTaskId === toId && l.toTaskId === fromId && l.type === 'blocking'),
  );
  if (exists) {
    toast('These tasks are already linked');
    return;
  }
  addTaskLink(fromId, toId, 'blocking');
  const db = getDb();
  toast(`“${db.tasks[fromId]?.name ?? 'Task'}” now blocks “${db.tasks[toId]?.name ?? 'task'}”`);
}

// ---------------------------------------------------------------------------
// Header

const GanttHeader = memo(function GanttHeader({ range, zoom, px, todayIdx }: { range: Range; zoom: Zoom; px: number; todayIdx: number }) {
  const months = useMemo(() => monthSegments(range, px), [range, px]);
  const units = useMemo(() => unitSegments(range, zoom, px, todayIdx), [range, zoom, px, todayIdx]);
  return (
    <div className="gv-thead" style={{ width: range.days * px }}>
      <div className="gv-tier gv-tier-top">
        {months.map((m) => (
          <div key={m.key} className="gv-tcell" style={{ left: m.x, width: m.w }}>
            {m.w >= 56 && <span className="gv-tlabel">{m.label}</span>}
          </div>
        ))}
      </div>
      <div className={clsx('gv-tier gv-tier-bottom', `z-${zoom}`)}>
        {units.map((u) => (
          <div key={u.key} className={clsx('gv-tcell', { weekend: u.weekend, today: u.today })} style={{ left: u.x, width: u.w }}>
            {zoom === 'day' ? (
              <>
                <span className="gv-wd">{u.sub}</span>
                <span className="gv-dn">{u.label}</span>
              </>
            ) : (
              <span className="gv-tlabel">{u.label}</span>
            )}
          </div>
        ))}
      </div>
      {todayIdx >= 0 && todayIdx < range.days && <div className="gv-today-head" style={{ left: todayIdx * px + px / 2 }} />}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Left pane rows

const LeftTaskRow = memo(function LeftTaskRow({
  task,
  row,
  depth,
  hasKids,
  expanded,
  dated,
  linkTarget,
  onToggle,
  onOpen,
  onJump,
}: {
  task: Task;
  row: number;
  depth: number;
  hasKids: boolean;
  expanded: boolean;
  dated: boolean;
  linkTarget: boolean;
  onToggle: (id: string) => void;
  onOpen: (id: string) => void;
  onJump: (id: string) => void;
}) {
  const status = useDb((db) => statusOf(db, task));
  const statuses = useDbShallow((db) => effectiveStatuses(db, task.listId));
  const closed = isCompleteType(status.type);
  return (
    <div
      className={clsx('gv-lrow', { closed, 'link-target': linkTarget })}
      data-row={row}
      data-link-task={task.id}
      onClick={() => onOpen(task.id)}
      onContextMenu={(e) => openContextMenu(e, (close) => <TaskContextMenu task={task} close={close} />)}
    >
      <span className="gv-indent" style={{ width: depth * 18 }} />
      <span className="gv-caret-slot">
        {hasKids && (
          <button
            className={clsx('gv-caret', { open: expanded })}
            onClick={(e) => {
              e.stopPropagation();
              onToggle(task.id);
            }}
          >
            <ChevronRight size={13} />
          </button>
        )}
      </span>
      <Popover
        content={(close) => (
          <StatusMenu
            statuses={statuses}
            currentId={task.statusId}
            onPick={(s) => {
              updateTask(task.id, { statusId: s.id });
              close();
            }}
          />
        )}
      >
        <button className="gv-status-btn" title={status.name}>
          <StatusIcon type={status.type} color={status.color} progress={statusProgress(statuses, status)} />
        </button>
      </Popover>
      <TaskTypeIcon task={task} size={14} />
      <span className="gv-lname truncate">{task.name}</span>
      {dated ? (
        <button
          className="gv-jump"
          title="Scroll to bar"
          onClick={(e) => {
            e.stopPropagation();
            onJump(task.id);
          }}
        >
          <Crosshair size={12} />
        </button>
      ) : (
        <span className="gv-nodate">No dates</span>
      )}
    </div>
  );
});

function AddTaskRow({ location, group, active, onActivate, onDone }: { location: Location; group: TaskGroup | undefined; active: boolean; onActivate: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  if (!active)
    return (
      <button className="gv-add" onClick={onActivate}>
        <Plus size={14} /> Add Task
      </button>
    );
  const save = () => {
    if (!name.trim()) return;
    createTaskInGroup(location, group, name);
    setName('');
  };
  return (
    <div className="gv-add editing">
      <StatusIcon type={group?.status?.type ?? 'open'} color={group?.status?.color ?? 'var(--text-3)'} />
      <input
        autoFocus
        value={name}
        placeholder="Task name — Enter to save"
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') save();
          if (e.key === 'Escape') onDone();
        }}
        onBlur={() => !name.trim() && setTimeout(onDone, 120)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bars

const GanttBar = memo(function GanttBar({
  task,
  row,
  x1,
  x2,
  info,
  dragging,
  linkTarget,
  onDown,
  onDotDown,
}: {
  task: Task;
  row: number;
  x1: number;
  x2: number;
  info: BarInfo;
  dragging: boolean;
  linkTarget: boolean;
  onDown: (e: React.PointerEvent, task: Task, mode: DragMode) => void;
  onDotDown: (e: React.PointerEvent, task: Task) => void;
}) {
  const ctx = (e: React.MouseEvent) => openContextMenu(e, (close) => <TaskContextMenu task={task} close={close} />);
  if (info.milestone) {
    const size = 16;
    return (
      <div
        className={clsx('gv-ms', { closed: info.closed, dragging, 'link-target': linkTarget })}
        data-row={row}
        data-link-task={task.id}
        style={{ left: x1, top: row * ROW_H + (ROW_H - size) / 2, width: x2 - x1, height: size }}
        onPointerDown={(e) => onDown(e, task, 'move')}
        onContextMenu={ctx}
        title={task.name}
      >
        <span className="gv-ms-shape" style={{ background: info.color }} />
        <span className="gv-dot" onPointerDown={(e) => onDotDown(e, task)} title="Drag to another task to add a dependency" />
        <span className="gv-bar-out">{task.name}</span>
      </div>
    );
  }
  const w = Math.max(4, x2 - x1);
  const inside = w >= LABEL_INSIDE_MIN;
  return (
    <div
      className={clsx('gv-bar', { closed: info.closed, dragging, 'link-target': linkTarget, tiny: w < 18 })}
      data-row={row}
      data-link-task={task.id}
      style={{ left: x1, width: w, top: row * ROW_H + (ROW_H - BAR_H) / 2, height: BAR_H }}
      onPointerDown={(e) => onDown(e, task, 'move')}
      onContextMenu={ctx}
    >
      <div className="gv-bar-fill" style={{ background: info.color, color: readableOn(info.color) }}>
        {inside && <span className="gv-bar-label truncate">{task.name}</span>}
      </div>
      <span className="gv-handle l" onPointerDown={(e) => onDown(e, task, 'start')} />
      <span className="gv-handle r" onPointerDown={(e) => onDown(e, task, 'end')} />
      <span className="gv-dot" onPointerDown={(e) => onDotDown(e, task)} title="Drag to another task to add a dependency" />
      {!inside && <span className="gv-bar-out">{task.name}</span>}
    </div>
  );
});

// ---------------------------------------------------------------------------

export function GanttView({ view, location }: { view: View; location: Location }) {
  const [search, setSearch] = useState('');
  const s = view.settings;
  const zoom: Zoom = s.ganttZoom ?? 'week';
  const px = PX_PER_DAY[zoom];
  const q = useViewQuery(view, location, search);
  const taskTypes = useDb((db) => db.taskTypes);
  const taskLinks = useDb((db) => db.taskLinks);
  const grouped = s.groupBy !== 'none';
  const collapsed = useMemo(() => new Set(s.collapsedGroups), [s.collapsedGroups]);
  const { isExpanded, toggle: toggleExpand } = useSubtaskExpansion(s.subtasks);
  const todayTs = useMemo(() => startOfDay(new Date()).getTime(), []);

  const openTask = useOpenTask();
  const openRef = useRef(openTask);
  openRef.current = openTask;
  const onOpen = useCallback((id: string) => openRef.current(id), []);

  const [drag, setDrag] = useState<DragState | null>(null);
  const [linkDrag, setLinkDrag] = useState<LinkDrag | null>(null);
  const [ghost, setGhost] = useState<{ row: number; idx: number } | null>(null);
  const [adding, setAdding] = useState(false);
  const [leftW, setLeftW] = useState(readLeftWidth);

  const scrollRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);
  const hlLeft = useRef<HTMLDivElement>(null);
  const hlRight = useRef<HTMLDivElement>(null);

  // rows -----------------------------------------------------------------------------
  const rows = useMemo(() => {
    const out: GRow[] = [];
    for (const g of q.groups) {
      const isCollapsed = grouped && collapsed.has(g.key);
      if (grouped) out.push({ kind: 'group', key: `g:${g.key}`, group: g, collapsed: isCollapsed });
      if (isCollapsed) continue;
      const walk = (t: Task, depth: number) => {
        const kids = q.children.get(t.id) ?? [];
        const exp = kids.length > 0 && isExpanded(t.id);
        out.push({ kind: 'task', key: `${g.key}:${t.id}`, task: t, depth, hasKids: kids.length > 0, expanded: exp });
        if (exp) for (const k of kids) walk(k, depth + 1);
      };
      for (const t of g.tasks) walk(t, 0);
    }
    return out;
  }, [q, grouped, collapsed, isExpanded]);

  const info = useMemo(() => {
    const db = getDb();
    const m = new Map<string, BarInfo>();
    for (const r of rows) {
      if (r.kind !== 'task' || m.has(r.task.id)) continue;
      const st = statusOf(db, r.task);
      m.set(r.task.id, {
        color: st.color,
        closed: isCompleteType(st.type),
        milestone: !!r.task.taskTypeId && taskTypes[r.task.taskTypeId]?.builtin === 'milestone',
      });
    }
    return m;
  }, [rows, taskTypes]);

  // timeline range (grow-only while the view is mounted; frozen during drags) ---------------
  const bounds = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    let earliest = Infinity;
    for (const t of q.matched) {
      for (const v of [t.startDate, t.dueDate]) {
        if (v == null) continue;
        min = Math.min(min, v);
        max = Math.max(max, v);
      }
    }
    for (const r of rows) if (r.kind === 'task') for (const v of [r.task.startDate, r.task.dueDate]) if (v != null) earliest = Math.min(earliest, v);
    return { min: Math.min(min, todayTs), max: Math.max(max, todayTs), earliest: Number.isFinite(earliest) ? earliest : null };
  }, [q.matched, rows, todayTs]);

  const frozen = drag !== null || linkDrag !== null;
  const rangeRef = useRef<{ zoom: Zoom; range: Range } | null>(null);
  const range = useMemo(() => {
    const want = desiredRange(bounds.min, bounds.max, zoom);
    const prev = rangeRef.current;
    let next = want;
    if (prev && prev.zoom === zoom) {
      next = frozen ? prev.range : mergeRange(prev.range, want);
      if (next.start === prev.range.start && next.days === prev.range.days) return prev.range;
    }
    rangeRef.current = { zoom, range: next };
    return next;
  }, [bounds.min, bounds.max, zoom, frozen]);

  const timelineW = range.days * px;
  const todayIdx = dayIndex(range.start, todayTs);

  // geometry -------------------------------------------------------------------------------
  const { bars, geo } = useMemo(() => {
    const bars: { key: string; task: Task; row: number; g: Geo }[] = [];
    const geo = new Map<string, Geo>();
    rows.forEach((r, i) => {
      if (r.kind !== 'task') return;
      const t = drag && drag.taskId === r.task.id ? { ...r.task, ...applyDrag(r.task, drag.mode, drag.delta) } : r.task;
      const sp = spanOf(t, range.start);
      if (!sp) return;
      const milestone = info.get(r.task.id)?.milestone ?? false;
      let g: Geo;
      if (milestone) {
        const c = (sp.b + 0.5) * px;
        g = { row: i, a: sp.b, b: sp.b, x1: c - 9, x2: c + 9, milestone, start: t.dueDate ?? t.startDate, due: t.dueDate ?? t.startDate };
      } else {
        g = { row: i, a: sp.a, b: sp.b, x1: sp.a * px, x2: (sp.b + 1) * px, milestone, start: t.startDate ?? t.dueDate, due: t.dueDate ?? t.startDate };
      }
      bars.push({ key: r.key, task: r.task, row: i, g });
      if (!geo.has(r.task.id)) geo.set(r.task.id, g);
    });
    return { bars, geo };
  }, [rows, range.start, px, drag, info]);

  const deps = useMemo(() => {
    const out: { id: string; d: string; conflict: boolean; from: string; to: string }[] = [];
    for (const l of Object.values(taskLinks)) {
      if (l.type !== 'blocking') continue;
      const f = geo.get(l.fromTaskId);
      const t = geo.get(l.toTaskId);
      if (!f || !t) continue;
      const conflict = t.start !== null && f.due !== null && t.start < f.due;
      out.push({ id: l.id, from: l.fromTaskId, to: l.toTaskId, conflict, d: elbowPath(f.x2, f.row * ROW_H + ROW_H / 2, t.x1, t.row * ROW_H + ROW_H / 2) });
    }
    return out;
  }, [taskLinks, geo]);

  // live values for pointer handlers registered once
  const live = useRef({ px, rangeStart: range.start, geo, leftW });
  live.current = { px, rangeStart: range.start, geo, leftW };

  // scrolling ------------------------------------------------------------------------------
  const pendingScroll = useRef<{ ts: number; mode: 'left' | 'center'; smooth?: boolean } | null>(null);
  const didInit = useRef(false);
  const prevRange = useRef<{ start: number; zoom: Zoom } | null>(null);

  const scrollToTs = useCallback((ts: number, mode: 'left' | 'center', smooth = false) => {
    const el = scrollRef.current;
    if (!el) return;
    const { px, rangeStart, leftW } = live.current;
    const x = dayIndex(rangeStart, ts) * px;
    const visible = el.clientWidth - leftW;
    const left = mode === 'left' ? x : x + px / 2 - visible / 2;
    el.scrollTo({ left: Math.max(0, left), behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const prev = prevRange.current;
    prevRange.current = { start: range.start, zoom };
    if (!didInit.current) {
      didInit.current = true;
      pendingScroll.current = { ts: addDays(bounds.earliest ?? todayTs, -3).getTime(), mode: 'left' };
    } else if (prev && prev.zoom === zoom && prev.start !== range.start && !pendingScroll.current) {
      // the range grew to the left: keep the same dates under the viewport
      el.scrollLeft += dayIndex(range.start, prev.start) * px;
    }
    const p = pendingScroll.current;
    if (p) {
      pendingScroll.current = null;
      scrollToTs(p.ts, p.mode, p.smooth);
    }
  });

  const setZoom = (z: Zoom) => {
    if (z === zoom) return;
    const el = scrollRef.current;
    if (el) {
      const centerX = el.scrollLeft + (el.clientWidth - leftW) / 2;
      pendingScroll.current = { ts: dayTs(range.start, Math.floor(centerX / px)), mode: 'center' };
    }
    updateViewSettings(view.id, { ganttZoom: z });
  };

  const onJump = useCallback(
    (id: string) => {
      const g = live.current.geo.get(id);
      const el = scrollRef.current;
      if (!g || !el) return;
      el.scrollTo({ left: Math.max(0, g.x1 - 4 * live.current.px), behavior: 'smooth' });
    },
    [],
  );

  // hover highlight shared by both panes (DOM-only so moving the mouse doesn't re-render)
  const hoverRow = useRef(-1);
  const setHover = (idx: number) => {
    if (hoverRow.current === idx) return;
    hoverRow.current = idx;
    for (const el of [hlLeft.current, hlRight.current]) {
      if (!el) continue;
      el.style.display = idx < 0 ? 'none' : 'block';
      el.style.transform = `translateY(${idx * ROW_H}px)`;
    }
  };

  const autoScroll = (ev: PointerEvent) => {
    const el = scrollRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    if (ev.clientX > r.right - 40) el.scrollLeft += 14;
    else if (ev.clientX < r.left + live.current.leftW + 40) el.scrollLeft -= 14;
  };

  // bar drag (move / resize) -----------------------------------------------------------------
  const onBarDown = useCallback((e: React.PointerEvent, task: Task, mode: DragMode) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const el = scrollRef.current;
    if (!el) return;
    const { px, rangeStart } = live.current;
    const startX = e.clientX;
    const startScroll = el.scrollLeft;
    const lim = dragLimits(task, mode, rangeStart);
    let moved = false;
    let delta = 0;
    const onMove = (ev: PointerEvent) => {
      const dx = ev.clientX - startX + (el.scrollLeft - startScroll);
      if (!moved && Math.abs(dx) < 4) return;
      if (!moved) document.body.classList.add(mode === 'move' ? 'gv-moving' : 'gv-resizing');
      moved = true;
      autoScroll(ev);
      const d = Math.max(lim.min, Math.min(lim.max, Math.round(dx / px)));
      delta = d;
      setDrag((prev) => (prev && prev.delta === d ? prev : { taskId: task.id, mode, delta: d }));
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onUp);
      document.body.classList.remove('gv-moving', 'gv-resizing');
      setDrag(null);
      if (!moved) {
        if (mode === 'move') openRef.current(task.id);
        return;
      }
      if (delta !== 0) updateTask(task.id, applyDrag(task, mode, delta));
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onUp);
  }, []);

  // dependency drag ------------------------------------------------------------------------------
  const onDotDown = useCallback((e: React.PointerEvent, task: Task) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const body = bodyRef.current;
    const g = live.current.geo.get(task.id);
    if (!body || !g) return;
    const x1 = g.x2 + (g.milestone ? 2 : 6);
    const y1 = g.row * ROW_H + ROW_H / 2;
    const at = (ev: PointerEvent): LinkDrag => {
      const r = body.getBoundingClientRect();
      const hit = (document.elementFromPoint(ev.clientX, ev.clientY) as HTMLElement | null)?.closest<HTMLElement>('[data-link-task]');
      const id = hit?.dataset.linkTask;
      return { fromId: task.id, x1, y1, x2: ev.clientX - r.left, y2: ev.clientY - r.top, overId: id && id !== task.id ? id : null };
    };
    document.body.classList.add('gv-linking');
    const onMove = (ev: PointerEvent) => {
      autoScroll(ev);
      setLinkDrag(at(ev));
    };
    const onUp = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      document.body.classList.remove('gv-linking');
      const st = at(ev);
      setLinkDrag(null);
      if (st.overId) createDependency(task.id, st.overId);
    };
    const onCancel = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      window.removeEventListener('pointercancel', onCancel);
      document.body.classList.remove('gv-linking');
      setLinkDrag(null);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    window.addEventListener('pointercancel', onCancel);
  }, []);

  // left pane resize ---------------------------------------------------------------------------------
  const startLeftResize = (e: React.PointerEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startW = leftW;
    let w = startW;
    document.body.classList.add('gv-col-resizing');
    const onMove = (ev: PointerEvent) => {
      w = Math.max(200, Math.min(640, startW + ev.clientX - startX));
      setLeftW(w);
    };
    const onUp = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      document.body.classList.remove('gv-col-resizing');
      try {
        localStorage.setItem(LEFT_KEY, String(Math.round(w)));
      } catch {
        /* storage unavailable */
      }
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const toggleGroup = (key: string) =>
    updateViewSettings(view.id, { collapsedGroups: collapsed.has(key) ? s.collapsedGroups.filter((k) => k !== key) : [...s.collapsedGroups, key] });

  // undated rows: hover placeholder + click to schedule ------------------------------------------------
  const onStripMove = (e: React.MouseEvent, row: number) => {
    const body = bodyRef.current;
    if (!body) return;
    const idx = Math.floor((e.clientX - body.getBoundingClientRect().left) / px);
    setGhost((g) => (g && g.row === row && g.idx === idx ? g : { row, idx }));
  };
  const scheduleAt = (task: Task, idx: number) => {
    updateTask(task.id, { startDate: dayTs(range.start, idx), dueDate: dayTs(range.start, idx + 2) });
    setGhost(null);
  };

  const removeDependency = (e: React.MouseEvent, linkId: string, from: string, to: string) => {
    const db = getDb();
    openContextMenu(e, (close) => (
      <Menu style={{ width: 260 }}>
        <MenuLabel>
          <span className="truncate">
            {db.tasks[from]?.name} → {db.tasks[to]?.name}
          </span>
        </MenuLabel>
        <MenuItem
          icon={<Unlink size={15} />}
          label="Remove dependency"
          danger
          onClick={() => {
            removeTaskLink(linkId);
            close();
          }}
        />
      </Menu>
    ));
  };

  const bodyH = Math.max(1, rows.length + 1) * ROW_H + 80;
  const unit = zoom === 'day' ? px : 7 * px;
  const dragBar = drag ? bars.find((b) => b.task.id === drag.taskId) : undefined;
  const firstGroup = q.groups[0];
  const markerId = `gv-arrow-${view.id}`;

  const zoomControl = (
    <div className="gv-zoom" role="group" aria-label="Timeline scale">
      {(['day', 'week', 'month'] as Zoom[]).map((z) => (
        <button key={z} className={clsx('gv-zoom-btn', { on: zoom === z })} onClick={() => setZoom(z)}>
          {z === 'day' ? 'Day' : z === 'week' ? 'Week' : 'Month'}
        </button>
      ))}
    </div>
  );
  const todayButton = (
    <button className="toolbar-btn gv-today-btn" onClick={() => scrollToTs(todayTs, 'center', true)}>
      <CalendarClock size={14} /> Today
    </button>
  );

  return (
    <div className="gv">
      <ViewToolbar
        view={view}
        location={location}
        search={search}
        onSearch={setSearch}
        onAddTask={() => {
          setAdding(true);
          requestAnimationFrame(() => scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' }));
        }}
        features={{ group: true, subtasks: true, columns: false, sort: true }}
        leftExtra={zoomControl}
        rightExtra={todayButton}
      />
      <div
        className="gv-scroll"
        ref={scrollRef}
        style={{ '--gv-left': `${leftW}px`, '--gv-head-h': `${HEAD_H}px`, '--gv-row-h': `${ROW_H}px` } as React.CSSProperties}
        onMouseMove={(e) => {
          const r = (e.target as HTMLElement).closest?.('[data-row]') as HTMLElement | null;
          setHover(r ? Number(r.dataset.row) : -1);
        }}
        onMouseLeave={() => setHover(-1)}
      >
        <div className="gv-canvas" style={{ width: leftW + timelineW }}>
          {/* left: task tree */}
          <div className="gv-left" style={{ width: leftW }}>
            <div className="gv-left-head">
              <span className="gv-left-title">Name</span>
              <span className="gv-left-count">{q.total}</span>
            </div>
            <div className="gv-left-body">
              <div className="gv-hl" ref={hlLeft} />
              {rows.map((r, i) =>
                r.kind === 'group' ? (
                  <div key={r.key} className="gv-lgroup" data-row={i}>
                    <button className={clsx('gv-caret', { open: !r.collapsed })} onClick={() => toggleGroup(r.group.key)}>
                      <ChevronRight size={14} />
                    </button>
                    <GroupLabel group={r.group} />
                    <span className="gv-gcount">{r.group.tasks.length}</span>
                  </div>
                ) : (
                  <LeftTaskRow
                    key={r.key}
                    task={r.task}
                    row={i}
                    depth={r.depth}
                    hasKids={r.hasKids}
                    expanded={r.expanded}
                    dated={r.task.startDate != null || r.task.dueDate != null}
                    linkTarget={linkDrag?.overId === r.task.id}
                    onToggle={toggleExpand}
                    onOpen={onOpen}
                    onJump={onJump}
                  />
                ),
              )}
              <AddTaskRow location={location} group={firstGroup} active={adding} onActivate={() => setAdding(true)} onDone={() => setAdding(false)} />
              {rows.length === 0 && <div className="gv-empty">No tasks match this view.</div>}
            </div>
            <span className="gv-left-resize" onPointerDown={startLeftResize} />
          </div>

          {/* right: timeline */}
          <div className="gv-right" style={{ width: timelineW }}>
            <GanttHeader range={range} zoom={zoom} px={px} todayIdx={todayIdx} />
            <div
              className={clsx('gv-body', `z-${zoom}`)}
              ref={bodyRef}
              style={{ width: timelineW, minHeight: bodyH, backgroundSize: `${unit}px 100%` }}
            >
              {zoom !== 'month' &&
                Array.from({ length: Math.ceil(range.days / 7) }, (_, w) => <div key={w} className="gv-weekend" style={{ left: (w * 7 + 5) * px, width: 2 * px }} />)}
              <div className="gv-hl" ref={hlRight} />
              {rows.map((r, i) => {
                if (r.kind === 'group') {
                  let a = Infinity;
                  let b = -Infinity;
                  for (const t of r.group.tasks) {
                    const sp = spanOf(t, range.start);
                    if (sp) {
                      a = Math.min(a, sp.a);
                      b = Math.max(b, sp.b);
                    }
                  }
                  return (
                    <div key={r.key} className="gv-band" data-row={i} style={{ top: i * ROW_H }}>
                      {Number.isFinite(a) && <span className="gv-gspan" style={{ left: a * px, width: (b - a + 1) * px }} />}
                    </div>
                  );
                }
                const undated = r.task.startDate == null && r.task.dueDate == null;
                return (
                  <div
                    key={r.key}
                    className={clsx('gv-strip', { undated })}
                    data-row={i}
                    style={{ top: i * ROW_H }}
                    onMouseMove={undated ? (e) => onStripMove(e, i) : undefined}
                    onMouseLeave={undated ? () => setGhost(null) : undefined}
                    onClick={undated ? () => ghost && ghost.row === i && scheduleAt(r.task, ghost.idx) : undefined}
                  />
                );
              })}
              {ghost && rows[ghost.row]?.kind === 'task' && (
                <div className="gv-ghost" style={{ left: ghost.idx * px, width: Math.max(3 * px, 22), top: ghost.row * ROW_H + (ROW_H - BAR_H) / 2, height: BAR_H }}>
                  <Plus size={13} />
                </div>
              )}
              {todayIdx >= 0 && todayIdx < range.days && <div className="gv-today" style={{ left: todayIdx * px + px / 2 }} />}

              <svg className="gv-deps" width={timelineW} height={bodyH}>
                <defs>
                  <marker id={markerId} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L8,4 L0,8 z" className="gv-arrowhead" />
                  </marker>
                  <marker id={`${markerId}-bad`} viewBox="0 0 8 8" refX="7" refY="4" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
                    <path d="M0,0 L8,4 L0,8 z" className="gv-arrowhead bad" />
                  </marker>
                </defs>
                {deps.map((d) => (
                  <g key={d.id} className={clsx('gv-dep', { bad: d.conflict })}>
                    <path className="gv-dep-hit" d={d.d} onClick={(e) => removeDependency(e, d.id, d.from, d.to)} onContextMenu={(e) => removeDependency(e, d.id, d.from, d.to)}>
                      <title>{d.conflict ? 'Dependency conflict: the blocked task starts before this one is due' : 'Blocking dependency'}</title>
                    </path>
                    <path className="gv-dep-line" d={d.d} markerEnd={`url(#${d.conflict ? `${markerId}-bad` : markerId})`} />
                  </g>
                ))}
              </svg>

              {bars.map((b) => (
                <GanttBar
                  key={b.key}
                  task={b.task}
                  row={b.row}
                  x1={b.g.x1}
                  x2={b.g.x2}
                  info={info.get(b.task.id)!}
                  dragging={drag?.taskId === b.task.id}
                  linkTarget={linkDrag?.overId === b.task.id}
                  onDown={onBarDown}
                  onDotDown={onDotDown}
                />
              ))}

              {dragBar && (
                <div
                  className="gv-drag-tip"
                  style={dragBar.row === 0 ? { left: dragBar.g.x2 + 24, top: (ROW_H - 20) / 2 } : { left: dragBar.g.x1, top: dragBar.row * ROW_H - 23 }}
                >
                  {dragBar.g.milestone ? (
                    fmtDay(dragBar.g.due)
                  ) : (
                    <>
                      {fmtDay(dragBar.g.start)} → {fmtDay(dragBar.g.due)}
                      <span className="gv-drag-days">
                        {dragBar.g.start != null && dragBar.g.due != null ? `${differenceInCalendarDays(dragBar.g.due, dragBar.g.start) + 1}d` : ''}
                      </span>
                    </>
                  )}
                </div>
              )}

              {linkDrag && (
                <svg className="gv-linkline" width={timelineW} height={bodyH}>
                  <path d={`M${linkDrag.x1},${linkDrag.y1} L${linkDrag.x2},${linkDrag.y2}`} />
                  <circle cx={linkDrag.x2} cy={linkDrag.y2} r={4} />
                </svg>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
